/**
 * The grid's filter query-string encoding, read side.
 *
 * Split out of ``history.ts`` so that reading the format does not drag in the
 * history listener: ``history.ts`` imports ``applyHistoryUpdate`` from
 * ``reducers.ts``, which builds its initial state from ``defaults.ts``, which
 * needs to read the URL -- a cycle that throws ``getFilterState is not a
 * function`` for whichever of the three a bundle happens to evaluate first.
 * These two functions touch no module state, so keeping them here breaks it
 * and makes the format safe to import from anywhere, including site code.
 *
 * ``pushFilterStateChange`` stays in ``history.ts``: it is the write side and
 * genuinely needs the history object.
 */
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { IFilterState, IReduxState } from "./reducers.interfaces";

/**
 * Parses the grid's query-string encoding into `{filterID: values[]}`.
 *
 * This module owns that encoding -- `pushFilterStateChange` writes it and
 * `getFilterState` reads it back -- so anything else that needs to speak the
 * same format (e.g. a CMS-authored filter preset) should parse it through here
 * rather than reimplementing the walk, which would leave the two formats merely
 * identical by coincidence rather than by definition.
 *
 * Query params are expected in the format: ?filterID=value1,value2&anotherFilter=value3
 */
export const parseFilterQueryString = (
    search: string,
): Map<string, string[]> => {
    const parsed = new Map<string, string[]>();
    const params = new URLSearchParams(search.replace(/^\?/, ""));
    for (const [key, value] of params.entries()) {
        parsed.set(key, value.split(","));
    }
    return parsed;
};

/**
 * Reads filter state from the current URL query string.
 * Used to initialize Redux state on page load.
 *
 * @returns Filter state object with selected values for each filter
 */
export const getFilterState = (): IReduxState["filters"] => {
    const filters: { [filterID: string]: IFilterState } = {};
    const window = getWindow();
    if (!window) {
        return filters;
    }

    for (const [filterID, selectedValues] of parseFilterQueryString(
        window.location.search,
    )) {
        filters[filterID] = {
            isOpen: true,
            selectedValues: selectedValues,
        };
    }

    return filters;
};
